import React, { Component } from 'react';
import autoBindMethods from 'class-autobind-decorator';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import cx from 'classnames';
import { noop, get } from 'lodash';

import {
  MenuItem,
  DropdownButton,
  FormGroup,
  ControlLabel,
  InputGroup,
  HelpBlock,
} from 'react-bootstrap';

import { AppConstants } from '../../constants';
import { createInputContainer } from '../../containers';

import { IFormInput, IInputContainer, Value } from '../../interfaces';
const { EMPTY_FIELD } = AppConstants;

const EMPTY_OPTION = { id: '', name: EMPTY_FIELD };

interface ITemplate {
  id: string;
  name: string;
  [key: string]: any;
}

interface IGroupedCategory {
  id: string | null;
  name: string;
  order: number;
  templates: ITemplate[];
}

interface IProps extends IFormInput {
  addonAfter?: JSX.Element;
  addonBefore?: JSX.Element;
  allowEmpty?: boolean;
  groupedOptions?: IGroupedCategory[];
  optionsLabelKey?: string;
  optionsValueKey?: string;
}

interface IPropDefaults extends IProps {
  allowEmpty: boolean;
  onChange: (value: any) => void;
  groupedOptions: IGroupedCategory[];
  optionsLabelKey: string;
  optionsValueKey: string;
}

interface IPropsFormsy extends IPropDefaults, IInputContainer {}

@createInputContainer
@autoBindMethods
@observer
class GroupedDropdown extends Component<IProps, {}> {
  @observable private value: Value;
  @observable private isFocused = false;

  public static defaultProps: Partial<IProps> = {
    allowEmpty: false,
    className: '',
    disabled: false,
    groupedOptions: [],
    onChange: noop,
    optionsLabelKey: 'name',
    optionsValueKey: 'id',
    required: false,
    value: '',
  };

  get formsy () {
    return this.props as IPropsFormsy;
  }

  constructor (props: IPropsFormsy) {
    super(props);
    this.value = props.value;
  }

  private onChange (valueArg: any) {
    const value: string = valueArg;
    const { onChange, setValue } = this.formsy;
    this.value = value;
    setValue(value);
    onChange(value);
  }

  private onToggle (isOpen: boolean) {
    this.isFocused = isOpen;
  }

  private findSelectedTemplate (): ITemplate | undefined {
    const { groupedOptions, optionsValueKey } = this.formsy;
    for (const category of groupedOptions) {
      const template = category.templates.find(t => t[optionsValueKey] === this.value);
      if (template) {
        return template;
      }
    }
    return undefined;
  }

  public render () {
    const {
        addonAfter,
        addonBefore,
        allowEmpty,
        className,
        controlId,
        errorMessage,
        formGroupId,
        isDisabled,
        label,
        groupedOptions,
        optionsLabelKey,
        optionsValueKey,
        required,
        showErrorMessage,
        showRequiredMessage,
        validationState,
      } = this.formsy
      , selectedTemplate = this.findSelectedTemplate()
      , title = get(selectedTemplate, optionsLabelKey, '')
      , groupClassName = cx(
        className,
        'form-group-dropdown',
        'form-group-grouped-dropdown',
        { focused: this.isFocused },
        { 'has-value': !!this.value },
        { disabled: isDisabled },
      );

    const dropdownElement = (
      <DropdownButton disabled={isDisabled} id={`dropdown-${controlId}`} title={title} onToggle={this.onToggle}>
        {allowEmpty &&
          <MenuItem
            active={!this.value}
            eventKey={EMPTY_OPTION.id}
            onSelect={this.onChange}
          >
            {EMPTY_OPTION.name}
          </MenuItem>
        }

        {groupedOptions.map((category, categoryIdx) => (
          <React.Fragment key={category.id || `uncategorized-${categoryIdx}`}>
            <MenuItem header className='grouped-dropdown-category-header'>
              {category.name}
            </MenuItem>
            {category.templates.map(template => (
              <MenuItem
                key={template[optionsValueKey]}
                active={template[optionsValueKey] === this.value}
                eventKey={template[optionsValueKey]}
                onSelect={this.onChange}
                className='grouped-dropdown-template-item'
              >
                {template[optionsLabelKey]}
              </MenuItem>
            ))}
          </React.Fragment>
        ))}
      </DropdownButton>
    );

    return (
      <FormGroup id={formGroupId} controlId={controlId} className={groupClassName} validationState={validationState}>
        {label &&
          <ControlLabel>
            {label}
            {required &&
              <span className='required'>*</span>
            }
          </ControlLabel>
        }

        <InputGroup>
          {addonBefore}
          {dropdownElement}
          {addonAfter}
        </InputGroup>

        {showRequiredMessage &&
          <HelpBlock>{label || 'This field'} is required.</HelpBlock>}

        {showErrorMessage &&
          <HelpBlock>{errorMessage}</HelpBlock>}
      </FormGroup>
    );
  }
}

export default GroupedDropdown;
